import { Landmark, RefreshCw, ShieldCheck, Unlink } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { FinancialConnection } from '@/types'

interface ConnectionsListProps {
  connections: FinancialConnection[]
  isLoading?: boolean
  /** Show sync/disconnect controls (parents managing their own connections) */
  canManage?: boolean
  onSync?: () => void
  onDisconnect?: (connectionId: string) => void
  isSyncing?: boolean
}

type WatchState = 'watching' | 'attention' | 'paused'

function watchState(connection: FinancialConnection): WatchState {
  if (connection.status === 'ERROR' || connection.status === 'PENDING_REAUTH') return 'attention'
  if (connection.status === 'DISCONNECTED') return 'paused'
  return 'watching'
}

const dotClass: Record<WatchState, string> = {
  watching: 'bg-success-500',
  attention: 'bg-warning-500',
  paused: 'bg-gray-300',
}

function WatchPill({ state }: { state: WatchState }) {
  if (state === 'watching') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-success-50 px-2.5 py-1 text-xs font-medium text-success-600">
        <ShieldCheck className="h-3.5 w-3.5" />
        Watching
      </span>
    )
  }
  if (state === 'attention') {
    return (
      <span className="inline-flex items-center rounded-full bg-warning-50 px-2.5 py-1 text-xs font-medium text-warning-600">
        Needs a reconnect
      </span>
    )
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
      Paused
    </span>
  )
}

function ConnectionSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 px-4 py-3">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-gray-100" />
      <div className="flex-1">
        <div className="h-4 w-36 rounded bg-gray-100" />
        <div className="mt-1.5 h-3 w-48 rounded bg-gray-100" />
      </div>
      <div className="h-6 w-20 rounded-full bg-gray-100" />
    </div>
  )
}

/**
 * The "Bank connections" hearth-card: one row per institution with masked
 * accounts, a green watch dot, and a "Watching" pill when connected.
 */
export function ConnectionsList({
  connections,
  isLoading,
  canManage = false,
  onSync,
  onDisconnect,
  isSyncing,
}: ConnectionsListProps) {
  if (isLoading) {
    return (
      <div className="hearth-card divide-y divide-gray-100">
        <ConnectionSkeleton />
      </div>
    )
  }

  if (connections.length === 0) {
    return null
  }

  return (
    <div className="hearth-card divide-y divide-gray-100">
      {connections.map((connection) => {
        const state = watchState(connection)
        const masks = connection.accounts.map((account) => `••••${account.mask}`).join(' · ')

        return (
          <div key={connection.id} className="flex items-center gap-3 px-4 py-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600/10 text-primary-600">
              <Landmark className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">
                {connection.institution_name}
              </p>
              <p className="truncate text-xs text-gray-500">
                {connection.institution_name} {masks}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={cn('h-2 w-2 rounded-full', dotClass[state])} />
              <WatchPill state={state} />
              {canManage && onSync && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => onSync()}
                  disabled={isSyncing}
                >
                  <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
                  <span className="sr-only">{isSyncing ? 'Syncing…' : 'Sync now'}</span>
                </Button>
              )}
              {canManage && onDisconnect && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-gray-400 hover:text-error-600"
                  onClick={() => onDisconnect(connection.id)}
                >
                  <Unlink className="h-4 w-4" />
                  <span className="sr-only">Disconnect</span>
                </Button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
